/* eslint-disable no-async-promise-executor */
import { oneGraph } from '@chegg-wt/core/api/oneGraph'

import { ExpressKeys_expressKeys } from '@chegg-wt/core/oneGraphQueries/__generated__/ExpressKeys'
import Pusher from './pusher'

/**
 * PusherManager ensures that a single pusher connection
 * is persisted. The class is intended to be used as a singleton.
 *
 */
class PusherManager {
  private pusherClient: Pusher | null = null

  private expressKeys: Omit<ExpressKeys_expressKeys, '__typename'> | null = null

  private initializingPromise: Promise<void> | null = null

  private alreadyInitialized: boolean = false

  private subscribedChannels: Record<string, boolean> = {}

  public initialize = async (): Promise<void> => {
    if (this.alreadyInitialized) return
    if (this.initializingPromise) {
      return this.initializingPromise
    }

    this.initializingPromise = new Promise(async (resolve, reject) => {
      try {
        const { appKey, channelName, cluster, traceId } = await oneGraph.getExpressKeys()
        this.expressKeys = { appKey, channelName, cluster, traceId }

        this.pusherClient = new Pusher()
        await this.pusherClient.initialize({
          appKey,
          cluster,
        })
        await this.pusherClient.subscribe(channelName)
        this.alreadyInitialized = true
        resolve()
      } catch (err) {
        // Setting it to null so, that we can retry pusher connection on next request and not use same initializingPromise
        this.initializingPromise = null
        reject(err.message)
      }
    })

    return this.initializingPromise
  }

  public getTraceId = async (): Promise<string> => {
    await this.initialize()
    return this.expressKeys?.traceId
  }

  public subscribe = async (eventName, callback: (data: any) => void): Promise<void> => {
    if (this.subscribedChannels[eventName]) return
    await this.initialize()

    if (!this.pusherClient?.channel) {
      return
    }
    this.pusherClient.channel.bind(eventName, callback)
    this.subscribedChannels[eventName] = true
  }

  public unsubscribe = async (): Promise<void> => {
    await this.pusherClient?.unsubscribe(this.expressKeys?.channelName)
    this.pusherClient = null
    this.expressKeys = null
    this.initializingPromise = null
    this.alreadyInitialized = false
    this.subscribedChannels = {}
  }

  public unbind = (eventName, callback?) => {
    if (!this.pusherClient?.channel) {
      return
    }
    this.pusherClient.channel.unbind(eventName, callback)
    this.subscribedChannels[eventName] = false
  }
}

const pusherManager = new PusherManager()
export default pusherManager
